/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes, useParams, useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import './i18n';
import JsBarcode from 'jsbarcode';
import CookieConsent from './components/CookieConsent';
import { articleHtml } from './ArticleHtml';

const languagesList = [
  { code: 'en', label: 'English' }, { code: 'ru', label: 'Русский' },
  { code: 'pt', label: 'Português' }, { code: 'ar', label: 'العربية' },
  { code: 'bn', label: 'বাংলা' }, { code: 'da', label: 'Dansk' },
  { code: 'de', label: 'Deutsch' }, { code: 'el', label: 'Ελληνικά' },
  { code: 'es', label: 'Español' }, { code: 'fi', label: 'Suomi' },
  { code: 'fr', label: 'Français' }, { code: 'hi', label: 'हिन्दी' },
  { code: 'hu', label: 'Magyar' }, { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'it', label: 'Italiano' }, { code: 'ja', label: '日本語' },
  { code: 'jv', label: 'Basa Jawa' }, { code: 'ko', label: '한국어' },
  { code: 'ms', label: 'Bahasa Melayu' }, { code: 'nb', label: 'Norsk Bokmål' },
  { code: 'nl', label: 'Nederlands' }, { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'pl', label: 'Polski' }, { code: 'ro', label: 'Română' },
  { code: 'sv', label: 'Svenska' }, { code: 'ta', label: 'தமிழ்' },
  { code: 'th', label: 'ไทย' }, { code: 'tr', label: 'Türkçe' },
  { code: 'ur', label: 'اردو' }, { code: 'vi', label: 'Tiếng Việt' },
  { code: 'zh-cn', label: '简体中文' },
];

function BarcodeApp() {
  const { t, i18n } = useTranslation();

  const [standard, setStandard] = useState('CODE128');
  const [data, setData] = useState('7640141071231');
  const [height, setHeight] = useState(100);
  const [width, setWidth] = useState(2);
  const [showText, setShowText] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const barcodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (barcodeRef.current) {
      if (!data) {
        setError(null);
        barcodeRef.current.innerHTML = '';
        return;
      }
      try {
        setError(null);
        if (standard !== 'QR') {
            JsBarcode(barcodeRef.current, data, {
              format: standard,
              width: width,
              height: height,
              displayValue: showText,
              lineColor: "#000000",
              background: "transparent",
              margin: 0,
              valid: function(valid) {
                if (!valid) {
                  setError(t('error_invalid_format', { data: data, standard: standard }));
                }
              }
            });
        }
      } catch (e: any) {
        setError(t('error_invalid_data'));
        barcodeRef.current.innerHTML = '';
      }
    }
  }, [data, standard, height, width, showText]);

  const handleDownloadSVG = () => {
    if (!barcodeRef.current || error) return;
    const svgData = new XMLSerializer().serializeToString(barcodeRef.current);
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `barcode-${data}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPNG = () => {
    if (!barcodeRef.current || error) return;
    const svgData = new XMLSerializer().serializeToString(barcodeRef.current);
    const canvas = document.createElement("canvas");
    // Get actual dimensions of the SVG element
    const svgSize = barcodeRef.current.getBoundingClientRect();
    // Scale up for better resolution (e.g. 3x)
    const scale = 3;
    canvas.width = svgSize.width * scale;
    canvas.height = svgSize.height * scale;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const link = document.createElement("a");
        link.download = `barcode-${data}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [{
                "@type": "Question",
                "name": "${t('faq_1_title').replace(/"/g, '\\"')}",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${t('faq_1_desc').replace(/"/g, '\\"').replace(/<[^>]+>/g, '')}"
                }
              }, {
                "@type": "Question",
                "name": "${t('faq_2_title').replace(/"/g, '\\"')}",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${t('faq_2_desc').replace(/"/g, '\\"').replace(/<[^>]+>/g, '')}"
                }
              }, {
                "@type": "Question",
                "name": "${t('faq_3_title').replace(/"/g, '\\"')}",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${t('faq_3_desc').replace(/"/g, '\\"').replace(/<[^>]+>/g, '')}"
                }
              }]
            }
          `}
        </script>
      </Helmet>
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-2 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4">{t('h1_title')}</h1>
        <p className="text-white/70 max-w-2xl mx-auto">
          {t('h1_desc')}
        </p>
      </div>

      <main className="flex-grow flex flex-col lg:flex-row p-4 sm:p-8 gap-8 items-stretch max-w-6xl mx-auto w-full">
        {/* Sidebar Controls */}
        <div className="w-full lg:w-1/3 flex flex-col gap-4">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-md flex-grow">
            <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
              <span className="w-2 h-6 bg-indigo-500 rounded-full"></span>
              {t('generator_settings')}
            </h2>
            
            <div className="space-y-5">
              <div>
                <label htmlFor="standard-input" className="text-xs uppercase tracking-wider text-white/50 block mb-2 font-semibold">{t('barcode_standard')}</label>
                <select 
                  id="standard-input"
                  value={standard}
                  onChange={(e) => setStandard(e.target.value)}
                  className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 appearance-none"
                >
                  <option value="CODE128">{t('standard_code128')}</option>
                  <option value="EAN13">{t('standard_ean13')}</option>
                  <option value="UPC">{t('standard_upc')}</option>
                  <option value="ITF14">{t('standard_itf14')}</option>
                  <option value="MSI">{t('standard_msi')}</option>
                  <option value="pharmacode">{t('standard_pharmacode')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="data-input" className="text-xs uppercase tracking-wider text-white/50 block mb-2 font-semibold">{t('barcode_data')}</label>
                <textarea 
                  id="data-input"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                  className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 h-24 resize-none" 
                  placeholder="Enter text or numbers..."
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="height-input" className="text-xs uppercase tracking-wider text-white/50 block mb-2 font-semibold">{t('height')}</label>
                  <input 
                    id="height-input"
                    type="number" 
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500" 
                  />
                </div>
                <div>
                  <label htmlFor="width-input" className="text-xs uppercase tracking-wider text-white/50 block mb-2 font-semibold">{t('module_width')}</label>
                  <input 
                    id="width-input"
                    type="number" 
                    value={width}
                    onChange={(e) => setWidth(Number(e.target.value))}
                    className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500" 
                    step="0.5"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 py-2">
                <input 
                  type="checkbox" 
                  id="text-incl" 
                  checked={showText}
                  onChange={(e) => setShowText(e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-slate-900 accent-indigo-500" 
                />
                <label htmlFor="text-incl" className="text-sm text-white/80 select-none cursor-pointer">{t('show_text')}</label>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-grow flex flex-col gap-8 min-w-0">
          <div className="flex-grow bg-white border border-white/10 rounded-3xl p-6 sm:p-12 flex flex-col items-center justify-center shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-indigo-400/20 rounded-full blur-3xl group-hover:bg-indigo-400/30 transition-all"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl group-hover:bg-blue-400/30 transition-all"></div>

            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-xl border border-slate-200 relative z-10 w-full max-w-lg flex flex-col items-center overflow-x-auto min-h-[200px] justify-center">
              {error ? (
                <div className="text-center p-4">
                  <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                  </div>
                  <p className="text-red-500 font-semibold">{error}</p>
                </div>
              ) : (
                <svg ref={barcodeRef} className="max-w-full h-auto object-contain"></svg>
              )}
            </div>
            
            <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-4 relative z-10 w-full sm:w-auto">
              <button 
                onClick={handleDownloadSVG}
                disabled={!!error}
                className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${error ? 'bg-indigo-600/50 text-white/50 cursor-not-allowed shadow-none' : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/25'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                {t('download_svg')}
              </button>
              <button 
                onClick={handleDownloadPNG}
                disabled={!!error}
                className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${error ? 'bg-indigo-600/50 text-white/50 cursor-not-allowed shadow-none' : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/25'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                {t('download_png', 'Download PNG')}
              </button>
              <button 
                onClick={() => {
                  if (barcodeRef.current && !error) {
                    navigator.clipboard.writeText(barcodeRef.current.outerHTML).catch(console.error);
                  }
                }}
                disabled={!!error}
                className={`flex-1 sm:flex-none px-6 py-3 border rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-95 ${error ? 'border-slate-700/50 bg-slate-900/50 text-white/50 cursor-not-allowed' : 'bg-slate-900 border-slate-700 text-white hover:bg-slate-800'}`}
              >
                {t('copy_clipboard')}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* EEAT and Info Content */}
      <section className="w-full bg-slate-900 border-t border-white/10 pt-8 pb-16 px-4 sm:px-8 mt-auto">
        <div className="max-w-4xl mx-auto space-y-12 text-white/80">
          
          {i18n.language === 'en' && (
            <div className="article-content space-y-6" dangerouslySetInnerHTML={{ __html: articleHtml }} />
          )}

          {/* Existing Content */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4"><span dangerouslySetInnerHTML={{ __html: t('how_to_title') }} /></h2>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li><span dangerouslySetInnerHTML={{ __html: t('how_to_1') }} /></li>
              <li><span dangerouslySetInnerHTML={{ __html: t('how_to_2') }} /></li>
              <li><span dangerouslySetInnerHTML={{ __html: t('how_to_3') }} /></li>
            </ol>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4"><span dangerouslySetInnerHTML={{ __html: t('supported_title') }} /></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-indigo-400 mb-2"><span dangerouslySetInnerHTML={{ __html: t('supported_ean_upc_title') }} /></h3>
                <p className="text-sm"><span dangerouslySetInnerHTML={{ __html: t('supported_ean_upc_desc') }} /></p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-indigo-400 mb-2"><span dangerouslySetInnerHTML={{ __html: t('supported_code128_title') }} /></h3>
                <p className="text-sm"><span dangerouslySetInnerHTML={{ __html: t('supported_code128_desc') }} /></p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-indigo-400 mb-2"><span dangerouslySetInnerHTML={{ __html: t('supported_code39_title') }} /></h3>
                <p className="text-sm"><span dangerouslySetInnerHTML={{ __html: t('supported_code39_desc') }} /></p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-indigo-400 mb-2"><span dangerouslySetInnerHTML={{ __html: t('supported_itf14_title') }} /></h3>
                <p className="text-sm"><span dangerouslySetInnerHTML={{ __html: t('supported_itf14_desc') }} /></p>
              </div>
            </div>
          </div>

          <div className="bg-indigo-600/10 border border-indigo-500/20 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-3"><span dangerouslySetInnerHTML={{ __html: t('warning_title') }} /></h2>
            <p className="text-sm mb-4">
              <span dangerouslySetInnerHTML={{ __html: t('warning_desc1') }} />
            </p>
            <p className="text-sm">
              {t('warning_desc2')} <a href="https://www.gs1.org" target="_blank" rel="noopener noreferrer" className="text-indigo-400 underline">GS1.org</a>
            </p>
          </div>

        </div>
      </section>
    </>
  );
}

import { lazy, Suspense } from 'react';
import Layout from './components/Layout';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import MobileApp from './pages/MobileApp';
import Contacts from './pages/Contacts';
import SiteMap from './pages/SiteMap';

const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));

const EnRedirect = () => {
  const location = useLocation();
  const rest = location.pathname.substring(3); // remove "/en"
  return <Navigate to={rest || '/'} replace />;
};

function GaPageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });
    }
  }, [location]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <GaPageTracker />
        <Routes>
          <Route path="/admix" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AdminLogin /></Suspense>} />
          <Route path="/admix/dashboard" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AdminDashboard /></Suspense>} />
          <Route path="/en/*" element={<EnRedirect />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<BarcodeApp />} />
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="mobile" element={<MobileApp />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="sitemap" element={<SiteMap />} />

            <Route path=":lang">
              <Route index element={<BarcodeApp />} />
              <Route path="terms" element={<Terms />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="mobile" element={<MobileApp />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="sitemap" element={<SiteMap />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}
